/**
 * Shared constants, types, and localStorage utilities for the Saved List feature.
 */
import type { Apartment } from '../../types/apartment';
import { resolvePhotoUrl, type PhotoPreset } from '../../types/apartmentCanonical';
import { safeLocal } from '@/utils/safeStorage';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/hooks/use-toast';

export const API_BASE = window.location.origin;
export const SAVES_KEY = 'tacostreet_local_saves';
export const NOPES_KEY = 'tacostreet_local_nopes';
export const NOTES_KEY = 'tacostreet_local_notes';

// Phase 4 — anonymous save cap + account-merge state
export const ANONYMOUS_SAVE_LIMIT = 10;

// Plan 2026-07-07 §5 — ONE cap shared by Comparison selection and Price
// Report submission (10). Comparison narrows toward what gets submitted, so
// a mismatched pair would let a renter compare a different-sized set than
// they can submit. Saving itself stays uncapped for account holders — the
// throttle lives at the point of cost (Alex's manual pull), not collection.
export const REPORT_CAP = 10;
const MERGE_DONE_KEY = 'tacostreet_saves_merged';

/** Is the user currently logged in?
 *  Post audit #7 we no longer persist the JWT to localStorage — the
 *  authoritative auth signal is the HttpOnly cookie (invisible to JS) and
 *  the user metadata in Zustand state (set after /auth/me succeeds on
 *  page load). */
export function isLoggedIn(): boolean {
  if (typeof window === 'undefined') return false;
  return useAuthStore.getState().user !== null;
}

/** Can the user save another apartment? Logged-in users have no cap. */
export function canSaveMore(): boolean {
  if (isLoggedIn()) return true;
  return getLocalSaves().length < ANONYMOUS_SAVE_LIMIT;
}

/** Save/unsave feedback toast — the one shared voice for the core action.
 *  Call from every save toggle handler (search results, browse rail, vibe,
 *  saved list). TOAST_LIMIT=1 in use-toast means a rapid re-toggle replaces
 *  the previous toast instead of stacking. */
export function announceSaveToggle(saved: boolean) {
  toast({
    description: saved ? 'Saved to your list 🌮' : 'Removed from your list',
    duration: 2000,
    className: 'ts-save-toast',
  });
}

/** Emits `saveLimitReached` so a page-level listener can show the
 *  "Your list is getting good." prompt. */
export function notifySaveLimitReached() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event('saveLimitReached'));
}

/** Merge localStorage saves + notes into the user's account.
 *  Used after login/signup. Deduped server-side by name+address. */
export async function mergeLocalSavesToAccount(token: string): Promise<number> {
  const saves = getLocalSaves();
  const notes = getLocalNotes();
  if (saves.length === 0) return 0;

  try {
    const res = await fetch(`${API_BASE}/api/saved-apartments/bulk`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify({
        apartments: saves.map(apt => ({
          apartment_data: apt,
          user_notes: notes[apt.name || ''] || '',
        })),
      }),
    });
    if (!res.ok) return 0;
    const data = await res.json();
    safeLocal.set(MERGE_DONE_KEY, 'true');
    return Number(data?.merged || 0);
  } catch {
    return 0;
  }
}

export interface SavedItem {
  name: string;
  [key: string]: unknown;
}

// ─── Canonical save toggle ──────────────────────────────────────────
//
// THE one mutation path for saving/unsaving an Apartment. Owns, in one
// place, what four component-local implementations used to each half-do:
// the anonymous cap check (via canSaveMore — no hardcoded 10s), the
// localStorage mutation + change event (via setLocalSaves), analytics for
// BOTH save and unsave, and the shared toast. Components call this and
// read state via useSavedNames(); they do not touch SAVES_KEY directly.

import { useSyncExternalStore } from 'react';
import { trackEvent } from '@/hooks/useTracking';

export type SaveToggleStatus = 'saved' | 'removed' | 'limit';

export interface SaveToggleResult {
  status: SaveToggleStatus;
  /** The saved list after the toggle (unchanged when status==='limit'). */
  saves: SavedItem[];
}

export function toggleSaveApartment(
  apt: SavedItem,
  opts?: { source?: string; meta?: Record<string, unknown> },
): SaveToggleResult {
  const name = (apt?.name || '').trim();
  const saves = getLocalSaves();
  if (!name) return { status: 'removed', saves };

  const isSaved = saves.some((a) => a.name === name);
  const source = opts?.source || 'unknown';

  if (!isSaved && !canSaveMore()) {
    notifySaveLimitReached();
    return { status: 'limit', saves };
  }

  const next = isSaved
    ? saves.filter((a) => a.name !== name)
    : [...saves, apt];
  setLocalSaves(next); // persists + dispatches savedApartmentsChanged

  trackEvent(isSaved ? 'unsave' : 'save', source, {
    apartment_name: name,
    ...(opts?.meta || {}),
  });
  announceSaveToggle(!isSaved);

  return { status: isSaved ? 'removed' : 'saved', saves: next };
}

/** Seed several saves at once (the /my-list?s=<token> agent-shortlist flow).
 *  One localStorage write, one change event — NOT a toggleSaveApartment loop,
 *  which would fire N toasts and N analytics rows and trip the cap mid-list.
 *  Honors the anonymous cap; the caller announces the outcome. */
export function seedSaves(
  items: SavedItem[],
  opts?: { source?: string },
): { added: number; skippedCap: number } {
  const saves = getLocalSaves();
  const have = new Set(saves.map((a) => a.name));
  const room = isLoggedIn()
    ? Number.POSITIVE_INFINITY
    : Math.max(0, ANONYMOUS_SAVE_LIMIT - saves.length);
  const next = [...saves];
  let added = 0;
  let skippedCap = 0;
  for (const apt of items || []) {
    const name = (apt?.name || '').trim();
    if (!name || have.has(name)) continue;
    if (added >= room) { skippedCap += 1; continue; }
    next.push(apt);
    have.add(name);
    added += 1;
  }
  if (added > 0) {
    setLocalSaves(next); // persists + dispatches savedApartmentsChanged
    trackEvent('save', opts?.source || 'shortlist_seed', { count: added, seeded: true });
  }
  if (skippedCap > 0) notifySaveLimitReached();
  return { added, skippedCap };
}

// ─── Reactive read: useSavedNames ───────────────────────────────────
// Snapshot is cached against the raw localStorage string so
// useSyncExternalStore gets a stable reference between changes.
let _namesCacheRaw: string | null = null;
let _namesCache: string[] = [];

function _savedNamesSnapshot(): string[] {
  const raw = safeLocal.get(SAVES_KEY) || '[]';
  if (raw !== _namesCacheRaw) {
    _namesCacheRaw = raw;
    try {
      _namesCache = (JSON.parse(raw) as SavedItem[])
        .map((a) => a?.name)
        .filter((n): n is string => typeof n === 'string' && !!n);
    } catch {
      _namesCache = [];
    }
  }
  return _namesCache;
}

function _subscribeSaves(cb: () => void): () => void {
  window.addEventListener('savedApartmentsChanged', cb);
  window.addEventListener('storage', cb); // other tabs
  return () => {
    window.removeEventListener('savedApartmentsChanged', cb);
    window.removeEventListener('storage', cb);
  };
}

const EMPTY_SAVED_NAMES: string[] = [];
const savedNamesOnServer = () => EMPTY_SAVED_NAMES;

/** Names of currently saved Apartments — re-renders on any save change
 *  (this tab via savedApartmentsChanged, other tabs via storage). */
export function useSavedNames(): string[] {
  return useSyncExternalStore(_subscribeSaves, _savedNamesSnapshot, savedNamesOnServer);
}

let _itemsCacheRaw: string | null = null;
let _itemsCache: SavedItem[] = [];

function _savedItemsSnapshot(): SavedItem[] {
  const raw = safeLocal.get(SAVES_KEY) || '[]';
  if (raw !== _itemsCacheRaw) {
    _itemsCacheRaw = raw;
    try {
      _itemsCache = JSON.parse(raw) as SavedItem[];
    } catch {
      _itemsCache = [];
    }
  }
  return _itemsCache;
}

/** Full saved Apartment objects (e.g. for map pin layers) — same
 *  subscription semantics as useSavedNames. */
export function useSavedApartments(): SavedItem[] {
  return useSyncExternalStore(_subscribeSaves, _savedItemsSnapshot);
}

export const SORT_OPTIONS = [
  { value: 'saved', label: 'Recently Saved' },
  { value: 'price-asc', label: 'Price: Low to High' },
  { value: 'price-desc', label: 'Price: High to Low' },
  { value: 'rating', label: 'Highest Rated' },
  { value: 'name', label: 'A-Z' },
];

export function getLocalSaves(): SavedItem[] { try { return JSON.parse(safeLocal.get(SAVES_KEY) || '[]'); } catch { return []; } }
export function setLocalSaves(arr: SavedItem[]) {
  safeLocal.set(SAVES_KEY, JSON.stringify(arr));
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('savedApartmentsChanged'));
  }
}
export function getLocalNopes(): SavedItem[] { try { return JSON.parse(safeLocal.get(NOPES_KEY) || '[]'); } catch { return []; } }
export function setLocalNopes(arr: SavedItem[]) { safeLocal.set(NOPES_KEY, JSON.stringify(arr)); }
export function getLocalNotes(): Record<string, string> { try { return JSON.parse(safeLocal.get(NOTES_KEY) || '{}'); } catch { return {}; } }
export function setLocalNotes(obj: Record<string, string>) {
  safeLocal.set(NOTES_KEY, JSON.stringify(obj));
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('notesChanged'));
  }
}

/** Convert a photo path (local or external) to a CDN/R2 URL. */
export function toCdnUrl(p: unknown, preset: PhotoPreset = 'card'): string {
  return resolvePhotoUrl(p, preset) ?? '';
}

/** Get the first available photo URL for an apartment. */
export function getFirstPhotoUrl(apt: Apartment, preset: PhotoPreset = 'card'): string | null {
  const sources = [...(apt.local_photos || []), ...(apt.photos || [])];
  for (const p of sources) {
    const url = resolvePhotoUrl(p as unknown, preset);
    if (url) return url;
  }
  return null;
}
